import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { AppContext, AuthContext } from 'Context';
import { CloudServiceConnector } from './components/CloudServiceConnector';
import { FileListingArea } from './components/FileListingArea';
import { AffiliateMemberDropdown } from './components/AffiliateMemberDropdown';
import { DropboxFileBrowser } from './components/DropboxFileBrowser';
import { GoogleDriveFileBrowser } from './components/GoogleDriveFileBrowser';
import { handleDropboxOAuthCallback } from './utils/dropboxOAuthHandler';
import { handleGoogleDriveOAuthCallback } from './utils/googleDriveOAuthHandler';
import styles from './CloudUpload.module.css';

export const CloudUpload = () => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  // OAuth callbacks tagged with state=mobile_* originated from the native
  // app's system-browser auth flow. Dropbox requires an HTTPS redirect URI,
  // so the app uses this URL and we bounce to a custom scheme so the OS
  // hands control back. Web flows never set state to mobile_*, so this is
  // a no-op for browser users.
  const mobileHash = window.location.hash;
  console.log('MOBILE_HANDOFF::: bouncer entered', {
    hasHash: !!mobileHash,
    hashLength: mobileHash ? mobileHash.length : 0,
    hashPreview: mobileHash ? `${mobileHash.substring(0, 80)}...` : '',
  });
  if (mobileHash) {
    try {
      const mobileState = new URLSearchParams(mobileHash.slice(1)).get('state') || '';
      const isMobile = mobileState.startsWith('mobile_');
      console.log('MOBILE_HANDOFF::: parsed state', { mobileState, isMobile });
      if (isMobile) {
        const target = `projector://cloud-upload${mobileHash}`;
        console.log('MOBILE_HANDOFF::: redirecting to native app', {
          targetPreview: `${target.substring(0, 80)}...`,
        });
        window.location.replace(target);
        return null;
      }
      console.log('MOBILE_HANDOFF::: state is not mobile_*, falling through to web flow');
    } catch (err) {
      console.error('MOBILE_HANDOFF::: failed to parse hash', err);
    }
  } else {
    console.log('MOBILE_HANDOFF::: no hash present, falling through');
  }

  // Check if we're in a popup OAuth flow (opened by CloudUploadModal)
  // Use sessionStorage flag since window.opener can be null after OAuth redirect
  const hasOpener = !!window.opener;
  const hasLocalStorageFlag = !!localStorage.getItem('isOAuthPopup');
  const hasAccessToken = window.location.hash.includes('access_token');
  const isPopupOAuth = (hasOpener || hasLocalStorageFlag) && hasAccessToken;

  console.log('DEBUG_POPUP::: CloudUpload loaded', {
    hasOpener,
    hasLocalStorageFlag,
    hasAccessToken,
    isPopupOAuth,
    hash: `${window.location.hash.substring(0, 50)}...`,
  });

  if (isPopupOAuth) {
    console.log('DEBUG_POPUP::: Detected popup OAuth flow, processing token...');
    // Parse and save token to localStorage so parent can access it
    const { hash } = window.location;
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const state = params.get('state');

    // Determine service from state prefix (e.g., 'dropbox_abc123' or 'googledrive_xyz789')
    const service = state && state.startsWith('dropbox_') ? 'dropbox' : 'googledrive';
    console.log('DEBUG_POPUP::: Detected service from state', { state, service });

    // Save token to localStorage for parent to read
    if (accessToken) {
      console.log('DEBUG_POPUP::: Saving token to localStorage', { service, accessToken: `${accessToken.substring(0, 20)}...` });
      const saved = localStorage.getItem('cloudConnectedServices');
      const services = saved ? JSON.parse(saved) : { dropbox: null, googledrive: null };
      services[service] = { accessToken, email: 'Connected' };
      localStorage.setItem('cloudConnectedServices', JSON.stringify(services));

      // Signal parent that OAuth is complete
      localStorage.setItem('oauthPopupComplete', JSON.stringify({ service, accessToken, timestamp: Date.now() }));
      console.log('DEBUG_POPUP::: Set oauthPopupComplete signal in localStorage');

      // Clean up state
      sessionStorage.removeItem('dropboxOAuthState');
      sessionStorage.removeItem('googleDriveOAuthState');
      localStorage.removeItem('isOAuthPopup');
    } else {
      console.log('DEBUG_POPUP::: No access token found!', { accessToken, state });
    }

    // Close popup after short delay - try multiple times
    console.log('DEBUG_POPUP::: Scheduling window.close() attempts...');
    setTimeout(() => {
      console.log('DEBUG_POPUP::: First window.close() attempt');
      window.close();
      // If window.close() didn't work, try again
      setTimeout(() => {
        console.log('DEBUG_POPUP::: Checking if window closed...', { closed: window.closed });
        if (!window.closed) {
          console.log('DEBUG_POPUP::: Window still open, trying close again');
          window.close();
        }
      }, 500);
    }, 300);

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
      }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', color: '#4CAF50', marginBottom: '16px' }}>✓ Connected!</div>
          <div style={{ fontSize: '14px', color: '#999', marginBottom: '16px' }}>You can close this window and return to the previous tab.</div>
          <button
            onClick={() => window.close()}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              backgroundColor: '#5aa5ef',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  // Check if we should skip rendering (coming from UploadDropdown or gift flow)
  const isFromUploadDropdown = sessionStorage.getItem('uploadDropdownCloudConnect');
  const isFromGiftFlow = sessionStorage.getItem('giftFlowCloudConnect');
  const returnUrl = sessionStorage.getItem('oauthReturnUrl');

  // Initialize state from localStorage
  const [connectedServices, setConnectedServices] = useState(() => {
    const saved = localStorage.getItem('cloudConnectedServices');
    return saved ? JSON.parse(saved) : { dropbox: null, googledrive: null };
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropboxBrowser, setShowDropboxBrowser] = useState(false);
  const [showGoogleDriveBrowser, setShowGoogleDriveBrowser] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handleServiceConnect = (service, connectionData) => {
    const newServices = {
      ...connectedServices,
      [service]: connectionData,
    };
    setConnectedServices(newServices);
    // Persist to localStorage
    localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
    setAppSnackbar({
      isVisible: true,
      message: `Successfully connected to ${service}`,
      type: 'success',
    });
  };

  const handleServiceDisconnect = (service) => {
    try {
      if (!service) return;

      const newServices = {
        ...connectedServices,
        [service]: null,
      };
      setConnectedServices(newServices);

      // Persist to localStorage
      try {
        localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to save to localStorage:', e);
      }

      // Also remove files from disconnected service
      setSelectedFiles((prev) => prev.filter((file) => file.source !== service));

      // Format service name properly
      const serviceName = service === 'googledrive' ? 'Google Drive' : service.charAt(0).toUpperCase() + service.slice(1);

      // Use setTimeout to avoid potential race conditions with state updates
      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: `Disconnected from ${serviceName}`,
            type: 'success',
          });
        }
      }, 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error disconnecting service:', error);
      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: 'Failed to disconnect service',
            type: 'error',
          });
        }
      }, 0);
    }
  };

  const handleImportFiles = (service) => {
    if (service === 'dropbox' && connectedServices.dropbox) {
      setShowDropboxBrowser(true);
    } else if (service === 'googledrive' && connectedServices.googledrive) {
      setShowGoogleDriveBrowser(true);
    }
  };

  const handleFilesSelected = (files, source) => {
    const filesWithSource = files.map((file) => ({
      ...file,
      source,
    }));
    setSelectedFiles((prev) => [...prev, ...filesWithSource]);
    setShowDropboxBrowser(false);
    setShowGoogleDriveBrowser(false);
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handlePublish = async () => {
    if (selectedFiles.length === 0) {
      setAppSnackbar({
        isVisible: true,
        message: 'Please select at least one file to publish',
        type: 'error',
      });
      return;
    }

    if (!selectedMember) {
      setAppSnackbar({
        isVisible: true,
        message: 'Please select a member to upload for',
        type: 'error',
      });
      return;
    }

    setIsPublishing(true);

    // TODO: Implement the actual publish API call here
    // For now, just simulate a delay
    setTimeout(() => {
      setAppSnackbar({
        isVisible: true,
        message: 'Files published successfully!',
        type: 'success',
      });
      setIsPublishing(false);
      // Reset form
      setSelectedFiles([]);
      setSelectedMember(null);
    }, 2000);
  };

  const canPublish = selectedFiles.length > 0 && selectedMember && !isPublishing;

  // Handle OAuth callbacks on component mount
  useEffect(() => {
    const isFromUploadDropdown = sessionStorage.getItem('uploadDropdownCloudConnect');
    const returnUrl = sessionStorage.getItem('oauthReturnUrl');

    handleDropboxOAuthCallback((authData) => {
      // Auth successful, connect the service
      handleServiceConnect('dropbox', authData);

      // If coming from UploadDropdown or gift flow, redirect back immediately
      if ((isFromUploadDropdown || isFromGiftFlow) && returnUrl) {
        // Set flag to auto-open modal on return
        sessionStorage.setItem('autoOpenCloudModal', isFromUploadDropdown || isFromGiftFlow);
        sessionStorage.removeItem('uploadDropdownCloudConnect');
        sessionStorage.removeItem('giftFlowCloudConnect');
        sessionStorage.removeItem('oauthReturnUrl');
        navigate(returnUrl, { replace: true });
      }
    });

    handleGoogleDriveOAuthCallback((authData) => {
      // Auth successful, connect the service
      handleServiceConnect('googledrive', authData);

      // If coming from UploadDropdown or gift flow, redirect back immediately
      if ((isFromUploadDropdown || isFromGiftFlow) && returnUrl) {
        // Set flag to auto-open modal on return
        sessionStorage.setItem('autoOpenCloudModal', isFromUploadDropdown || isFromGiftFlow);
        sessionStorage.removeItem('uploadDropdownCloudConnect');
        sessionStorage.removeItem('giftFlowCloudConnect');
        sessionStorage.removeItem('oauthReturnUrl');
        navigate(returnUrl, { replace: true });
      }
    });
    // eslint-disable-next-line
  }, []);

  // Don't render the page if we're redirecting from dropdown or gift flow
  if ((isFromUploadDropdown || isFromGiftFlow) && returnUrl) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
      }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>Connecting...</div>
          <div style={{ fontSize: '14px', color: '#999' }}>Redirecting back...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cloudUploadContainer}>
      {/* Header - Close button top right */}
      <div className={styles.header}>
        <button
          className={styles.closeButton}
          onClick={() => navigate('/settings/manage-users')}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Left White Box */}
      <div className={styles.contentWrapper}>
        <div className={styles.mainContent}>
          {/* Cloud Service Connection Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Connect Cloud Storage</h2>
            <p className={styles.sectionDescription}>
              Connect Dropbox or Google Drive
            </p>
            <CloudServiceConnector
              connectedServices={connectedServices}
              onDisconnect={handleServiceDisconnect}
            />
          </section>

          {/* File Listing Area */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {selectedFiles.length === 0 ? 'Selected Files' : `${selectedFiles.length} File${selectedFiles.length === 1 ? '' : 's'}`}
            </h2>
            <p className={styles.sectionDescription}>
              {selectedFiles.length === 0 ? 'Import files from your cloud' : 'Ready to upload'}
            </p>
            <FileListingArea
              files={selectedFiles}
              connectedServices={connectedServices}
              onImport={handleImportFiles}
              onRemoveFile={handleRemoveFile}
            />
          </section>

          {/* Member Selection & Publish */}
          <div className={styles.publishSection}>
            <div>
              <h2 className={styles.sectionTitle}>Upload For</h2>
              <p className={styles.sectionDescription}>
                Select member
              </p>
            </div>
            <AffiliateMemberDropdown
              selectedMember={selectedMember}
              onSelectMember={setSelectedMember}
              getToken={getToken}
            />
            <button
              className={`${styles.publishButton} ${canPublish ? styles.publishButtonActive : ''}`}
              onClick={handlePublish}
              disabled={!canPublish}
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Dropbox File Browser Modal */}
      {showDropboxBrowser && (
        <DropboxFileBrowser
          accessToken={connectedServices.dropbox?.accessToken}
          onClose={() => setShowDropboxBrowser(false)}
          onFilesSelected={(files) => handleFilesSelected(files, 'dropbox')}
          onShowMessage={(message) => setAppSnackbar({
            isVisible: true,
            message,
            type: 'default',
          })}
        />
      )}

      {/* Google Drive File Browser Modal */}
      {showGoogleDriveBrowser && (
        <GoogleDriveFileBrowser
          accessToken={connectedServices.googledrive?.accessToken}
          onClose={() => setShowGoogleDriveBrowser(false)}
          onFilesSelected={(files) => handleFilesSelected(files, 'googledrive')}
          onShowMessage={(message) => setAppSnackbar({
            isVisible: true,
            message,
            type: 'default',
          })}
        />
      )}
    </div>
  );
};
